import create from 'zustand';

import { ReduxDevTools } from '../helpers/storeLogger';
import { ConditionalStatePersistence } from '../helpers/storePersister';
import { T_Coupon, T_Product, T_Voucher, T_EEOffer } from '../types';
import { localStorage } from '../helpers/localStorage';
import { NSB_SKYPOD_MONTHLY_AND_ONEOFF_FEE_ON_US, removeCacheCoupons } from '../config';
import { sessionStorage } from '../helpers/sessionStorage';

/**
 * Removes duplicate coupons based on couponCode
 * - couponCode is treated as the business-unique identifier
 * - If duplicates exist, the LAST coupon wins
 */
const dedupeCouponsByCode = (coupons: T_Coupon[] = []): T_Coupon[] => {
  return Array.from(new Map(coupons.map(coupon => [coupon.couponCode, coupon])).values());
};

interface CouponStore {
  coupons: T_Coupon[];
  ctCoupons: T_Coupon[];
  voucher: T_Voucher | null;
  pCoupons: T_Coupon[];
  eligibilityOffers: T_EEOffer;
  setPreviousStateCoupons: (pCoupons: T_Coupon[]) => void;
  clearPreviousStateCoupons: () => void;
  setPromoCodeVoucher: (voucher: T_Voucher) => void;
  clearPromoCodeVoucher: () => void;
  applyCouponToStore: (coupon: T_Coupon) => void;
  applyMultipleCouponsToStore: (coupon: T_Coupon[]) => void;
  applyMultipleCTCouponsToStore: (coupon: T_Coupon[]) => void;
  applyPreviousCouponsToStore: (coupon: T_Coupon) => void;
  clearMultipleCouponsToStore: (couponsToRemove: T_Coupon[]) => void;
  clearCoupon: (coupon: T_Coupon, track?: boolean) => void;
  clearCouponFromCode: (couponCode: string, track?: boolean) => void;
  clearPreviousCouponFromCode: (couponCode: string, track?: boolean) => void;
  getCouponForProduct: (product: Pick<T_Product, 'id'>) => T_Coupon | undefined;
  getSplitCoupons: (coupon: T_Coupon[]) => { bbCoupon?: T_Coupon[]; dthCoupon?: T_Coupon[]; oneOffFee?: T_Coupon[] };
  hasSameCouponTypeAndCategory: (coupon: T_Coupon) => boolean;
  removeAllCoupons: () => void;
  getCouponFromCode: (couponCode: string) => T_Coupon | undefined;
  setEligibilityOffers: (offers: T_EEOffer) => void;
  removeEligibilityOffers: () => void;
}

const storageStateName = 'Coupon Store';

/**
 * Reads the persisted coupon state from the appropriate storage.
 * Prefers sessionStorage (CT-active sessions) and falls back to localStorage.
 * Also strips any removeCacheCoupons from the stored data.
 */
const getInitialCouponStorageState = (): Record<string, any> => {
  // Prefer session storage — populated when CT is/was active this session.
  const sessionRaw = sessionStorage.getItem(storageStateName);
  if (sessionRaw && sessionRaw !== '{}') {
    try {
      const parsed = JSON.parse(sessionRaw);
      if (parsed && Object.keys(parsed).length > 0) {
        if (parsed.coupons) {
          parsed.coupons = parsed.coupons.filter(
            (item: { couponCode: string }) => !removeCacheCoupons?.includes(item.couponCode)
          );
        }
        return parsed;
      }
    } catch {}
  }

  // Fall back to localStorage (non-CT / existing behaviour).
  let localData = JSON.parse(localStorage.getItem(storageStateName) ?? '{}');
  if (localData.coupons) {
    localData.coupons = dedupeCouponsByCode(
      localData.coupons?.filter((item: { couponCode: string }) => !removeCacheCoupons?.includes(item.couponCode)) ?? []
    );
    localStorage.setItem(storageStateName, JSON.stringify(localData));
  }
  return localData;
};

const getStorageState = getInitialCouponStorageState();

/**
 * Responsible for maintaining knowledge of the coupon that will be tied to konakart store. Adds to  storage.
 * IMPORTANT: Does not do any update to konakart. That should be done via order store.
 */
const [useCouponStore, couponStoreApi] = create<CouponStore>(
  ReduxDevTools(
    ConditionalStatePersistence(
      (set, get) => ({
        coupons: [],
        ctCoupons: [],
        pCoupons: [],
        voucher: null,
        eligibilityOffers: {
          eligibilityIncludeCoupons: [],
          eligibilityExcludeCoupons: [],
          eligibilityIncludedProductIds: null,
          eligibilityExcludedProductIds: null,
          eligibilityIncludeServiceCode: null,
          eligibilityExcludeServiceCode: null,
          eligibilityIncludeCampaign: null,
          eligibilityExcludeCampaign: null,
          allActiveCoupansBeforeEEOffer: [],
        },
        ...getStorageState,

        /* ----------------------------- APPLY COUPONS ----------------------------- */

        applyCouponToStore: newCoupon => {
          const { coupons } = get();

          const updatedCoupons = dedupeCouponsByCode([...coupons, newCoupon]);

          // If no change, coupon was duplicate
          if (updatedCoupons.length === coupons.length) return;

          set({ coupons: updatedCoupons }, `apply ${newCoupon.couponCode} to store`);
        },

        applyMultipleCouponsToStore: newCoupons => {
          const filteredCoupons = newCoupons.filter(cp => !removeCacheCoupons?.includes(cp.couponCode));

          const uniqueCoupons = dedupeCouponsByCode(filteredCoupons);

          set({ coupons: uniqueCoupons }, 'apply multiple coupons to store');
        },

        applyMultipleCTCouponsToStore: newCoupon => {
          const { coupons, eligibilityOffers } = get();

          /**
           * Fast coupon lookup map
           */
          const couponMap = new Map(newCoupon.map(coupon => [coupon.couponCode, coupon]));
          /**
           * Helper updater
           */
          const updateCoupons = (couponList: T_Coupon[] = []) =>
            couponList.map(coupon => {
              const matchedCoupon = couponMap.get(coupon.couponCode);
              return matchedCoupon ? { ...coupon, ...matchedCoupon } : coupon;
            });

          /**
           * Update all coupon collections
           */
          const updatedCoupons = updateCoupons(coupons);
          const updatedEligibilityIncludeCoupons = updateCoupons(eligibilityOffers.eligibilityIncludeCoupons ?? []);
          const updatedEligibilityExcludeCoupons = updateCoupons(eligibilityOffers.eligibilityExcludeCoupons ?? []);
          const updatedAllActiveCoupansBeforeEEOffer = updateCoupons(
            eligibilityOffers.allActiveCoupansBeforeEEOffer ?? []
          );

          set(
            {
              coupons: updatedCoupons,
              ctCoupons: [...newCoupon],
              eligibilityOffers: {
                ...eligibilityOffers,
                eligibilityIncludeCoupons: updatedEligibilityIncludeCoupons,
                eligibilityExcludeCoupons: updatedEligibilityExcludeCoupons,
                allActiveCoupansBeforeEEOffer: updatedAllActiveCoupansBeforeEEOffer,
              },
            },
            `apply multiple CT coupons to store`
          );
        },

        applyPreviousCouponsToStore: pCoupon => {
          const { pCoupons } = get();

          const updated = dedupeCouponsByCode([...pCoupons, pCoupon]);
          if (updated.length === pCoupons.length) return;

          set({ pCoupons: updated }, 'apply previous coupons to store');
        },

        /* ----------------------------- CLEAR COUPONS ------------------------------ */

        clearMultipleCouponsToStore: couponsToRemove => {
          const { coupons } = get();
          const codesToRemove = couponsToRemove.map(cp => cp.couponCode);

          const updatedCoupons = coupons.filter(cp => !codesToRemove.includes(cp.couponCode));
          const updatedCTCoupons = coupons.filter(cp => !codesToRemove.includes(cp.couponCode));

          if (updatedCoupons.length === coupons.length) return;

          set(
            { coupons: updatedCoupons, ctCoupons: updatedCTCoupons },
            `clear multiple coupons: ${codesToRemove.join(', ')}`
          );
        },

        clearCoupon: couponToRemove => {
          const { coupons, ctCoupons } = get();
          set(
            {
              coupons: coupons.filter(cp => cp.couponCode !== couponToRemove.couponCode),
              ctCoupons: ctCoupons.filter(cp => cp.couponCode !== couponToRemove.couponCode),
            },
            `clear ${couponToRemove.couponCode} coupon`
          );
        },

        clearCouponFromCode: couponCode => {
          const { coupons, ctCoupons } = get();
          set(
            {
              coupons: coupons.filter(cp => cp.couponCode !== couponCode),
              ctCoupons: ctCoupons.filter(cp => cp.couponCode !== couponCode),
            },
            `clear ${couponCode} coupon`
          );
        },

        clearPreviousCouponFromCode: couponCode => {
          const { pCoupons } = get();
          set(
            {
              pCoupons: pCoupons.filter(cp => cp.couponCode !== couponCode),
            },
            `clear ${couponCode} previous coupon`
          );
        },

        removeAllCoupons: () => {
          set({ coupons: [], ctCoupons: [] }, 'remove all coupons');
        },

        /* ------------------------------ VOUCHERS --------------------------------- */

        setPromoCodeVoucher: voucher => {
          set({ voucher });
        },

        clearPromoCodeVoucher: () => {
          set({ voucher: null }, 'remove voucher types');
        },

        setPreviousStateCoupons: pCoupons => {
          set({ pCoupons });
        },

        clearPreviousStateCoupons: () => {
          set({ pCoupons: [] }, 'remove previous state coupons');
        },

        /* ------------------------------ GETTERS ---------------------------------- */

        getCouponForProduct: product => {
          const { coupons } = get();
          return coupons.find(cp =>
            cp.custom1
              .split(',')
              .map(id => Number(id))
              .includes(product.id)
          );
        },

        getSplitCoupons: coupons => ({
          bbCoupon: coupons.filter(c => c.couponCode.includes('BB')),
          dthCoupon: coupons.filter(
            c => !c.couponCode.includes('BB') && !c.couponCode.includes(NSB_SKYPOD_MONTHLY_AND_ONEOFF_FEE_ON_US)
          ),
          oneOffFee: coupons.filter(c => c.couponCode.includes(NSB_SKYPOD_MONTHLY_AND_ONEOFF_FEE_ON_US)),
        }),

        getCouponFromCode: couponCode => {
          const { ctCoupons } = get();
          return ctCoupons.find(c => c.couponCode === couponCode);
        },

        hasSameCouponTypeAndCategory: coupon => {
          const { coupons } = get();
          return coupons.some(
            existing =>
              existing.custom3.toLowerCase() === coupon.custom3.toLowerCase() &&
              existing.custom5.toLowerCase() === coupon.custom5.toLowerCase()
          );
        },

        /* --------------------------- ELIGIBILITY OFFERS --------------------------- */

        setEligibilityOffers: offers => {
          set({ eligibilityOffers: offers }, 'set eligibility offers');
        },

        removeEligibilityOffers: () => {
          set(
            {
              eligibilityOffers: {
                eligibilityIncludeCoupons: [],
                eligibilityExcludeCoupons: [],
                eligibilityIncludedProductIds: null,
                eligibilityExcludedProductIds: null,
                eligibilityIncludeServiceCode: null,
                eligibilityExcludeServiceCode: null,
                eligibilityIncludeCampaign: null,
                eligibilityExcludeCampaign: null,
                allActiveCoupansBeforeEEOffer: [],
              },
            },
            'clear eligibility offers'
          );
        },
      }),
      storageStateName
    ),
    'Coupon Store'
  )
);

export { useCouponStore, couponStoreApi };
